import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Submit type options for form submissions
 * - 'save': Save and stay in edit mode
 * - 'saveAndClose': Save and navigate to list view
 * - 'saveAndPost': Save with post action and navigate to list view (Invoices only)
 */
export type SubmitType = 'save' | 'saveAndClose' | 'saveAndPost';

/**
 * Configuration for the useFormSubmission hook
 */
export interface UseFormSubmissionConfig {
  /** The route to navigate to when using saveAndClose or saveAndPost */
  listRoute: string;
  /** Function to generate the edit route from the created/updated ID */
  getEditRoute: (id: string | number) => string;
  /** Whether to support the saveAndPost option (default: false) */
  supportsSaveAndPost?: boolean;
}

/**
 * Result of successful form submission with navigation info
 */
export interface SubmitResult {
  success: boolean;
  id?: string | number;
}

/**
 * Return type for the useFormSubmission hook
 */
export interface UseFormSubmissionReturn {
  /** Current submit type */
  submitType: SubmitType;
  /** Set the submit type */
  setSubmitType: (type: SubmitType) => void;
  /** Handle navigation after successful submission */
  handlePostSubmitNavigation: (result: SubmitResult) => void;
  /** Get the save mode value for API (1 = save, 2 = post) */
  getSaveMode: () => number;
}

/**
 * Hook for managing form submission state and post-submit navigation
 *
 * @param config - Configuration for the hook
 * @returns Submit type state and navigation helpers
 *
 * @example
 * ```tsx
 * const { submitType, setSubmitType, handlePostSubmitNavigation, getSaveMode } = useFormSubmission({
 *   listRoute: routes.RNC_INVOICES,
 *   getEditRoute: (id) => `${routes.RNC_INVOICES}/${id}/edit`,
 *   supportsSaveAndPost: true,
 * });
 *
 * // In form onSubmit:
 * const formattedValues = { ...values, saveMode: getSaveMode() };
 * const result = await InvoiceService.create(formattedValues);
 * if (result.isSuccess && result.data?.id) {
 *   handlePostSubmitNavigation({ success: true, id: result.data.id });
 * }
 * ```
 */
export const useFormSubmission = (
  config: UseFormSubmissionConfig
): UseFormSubmissionReturn => {
  const navigate = useNavigate();
  const { listRoute, getEditRoute } = config;

  // Default to saveAndClose for most forms
  const [submitType, setSubmitType] = useState<SubmitType>('saveAndClose');

  /**
   * Handle navigation after successful form submission
   * Routes based on the current submitType:
   * - 'saveAndClose' or 'saveAndPost': Navigate to list view
   * - 'save': Navigate to edit view for the created/updated record
   */
  const handlePostSubmitNavigation = useCallback(
    (result: SubmitResult) => {
      if (!result.success || !result.id) {
        return;
      }

      if (submitType === 'saveAndClose' || submitType === 'saveAndPost') {
        navigate(listRoute);
      } else if (submitType === 'save') {
        navigate(getEditRoute(result.id));
      }
    },
    [submitType, navigate, listRoute, getEditRoute]
  );

  /**
   * Get the save mode value for API calls
   * 1 = regular save (draft)
   * 2 = save and post
   */
  const getSaveMode = useCallback((): number => {
    return submitType === 'saveAndPost' ? 2 : 1;
  }, [submitType]);

  return {
    submitType,
    setSubmitType,
    handlePostSubmitNavigation,
    getSaveMode,
  };
};
